import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { ArticleStepper } from '@/components/ArticleStepper/ArticleStepper';
import { ClientCommunicationDrawer } from '@/components/comments/ClientCommunicationDrawer/ClientCommunicationDrawer';
import { DefectsList } from '@/components/DefectsList';
import Loader from '@/components/Loader';
import {
  PageLayout,
  PageLayoutCenter,
  PageLayoutContent,
  PageLayoutHeader,
  PageLayoutLeftPart,
  PageLayoutLeftPartContent,
  PageLayoutRightPart,
  PageLayoutTopPart,
} from '@/components/PageLayout';
import AlertBar from '@/design_system/AlertBar';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import FoldableSection from '@/design_system/FoldableSection';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconEdit from '@/icons/Edit.svg';
import IconFile from '@/icons/File.svg';
import IconPlace from '@/icons/Place.svg';
import { isMissingCustomsData, useArticleConditionForm, useArticleName } from '@/models/article';
import { useProductAttributesByArticle } from '@/models/productAttribute';
import { getRequestorType, RequestWithRelations, useRequest } from '@/models/request';
import { ArticleWithRelations } from '@/models/request';
import { getOrganizations, hasPermission } from '@/models/user';
import { useWorkflowPublic } from '@/models/workflow';
import { ArticleServices } from '@/routes/Requests/components/ArticleServices/ArticleServices';
import { CommentsSection } from '@/routes/Requests/components/CommentsSection';
import {
  ArticleContextProvider,
  useArticleContext,
} from '@/routes/Requests/contexts/ArticleContext';
import {
  RequestContextProvider,
  useRequestContext,
} from '@/routes/Requests/contexts/RequestContext';
import { InitialConditionForm } from '@/routes/Requests/New/components/ConditionForm/InitialConditionForm';
import { AuthenticityForm } from '@/routes/Requests/Request/Article/AuthenticityForm';
import ClientComment from '@/routes/Requests/Request/Article/ClientComment';
import RequalificationComment from '@/routes/Requests/Request/Article/RequalificationComment';
import { WarrantyForm } from '@/routes/Requests/Request/Article/WarrantyForm';
import { ArticleActivities } from '@/routes/Requests/Request/components/Activities/ArticleActivities';
import { RequestBreadcrumb } from '@/routes/Requests/Request/components/RequestBreadcrumb/RequestBreadcrumb';
import { useCurrentSession } from '@/services/auth';
import { ErrorBoundary } from '@/services/sentry';
import { createBEMClasses } from '@/utils/classname';
import { useResetSimpleBlocker } from '@/utils/navigation';
import { refreshBrowserTabTitle } from '@/utils/refreshBrowserTabTitle';
import { useScrollIntoView } from '@/utils/useScrollIntoView';
import useViewPort from '@/utils/useViewport';

import { QualityControlForm } from '../../New/components/ConditionForm/QualityControlForm';

import ArticleActions from './components/ArticleActions';
import { ArticleInfoDrawer } from './components/ArticleInfo/ArticleInfoDrawer';
import { ArticleInfoSidePanel } from './components/ArticleInfo/ArticleInfoSidePanel';
import { ArticlePriceAndEstimatedDueDate } from './components/ArticleInfo/ArticlePriceAndEstimatedDueDate';
import ArticleTaskOrStepMessage from './components/ArticleTaskOrStepMessage';
import { ArticleHeader } from './ArticleHeader';

import './Article.css';

const { block, element } = createBEMClasses('article');

export const Article = () => {
  const { requestId, articleId } = useParams();
  const { data: request, isLoading: isRequestLoading } = useRequest(requestId);
  const { data: workflow } = useWorkflowPublic(request?.workflowId);
  const article = request?.allArticles.find(({ id }) => id === articleId);
  const { data: conditionFormData } = useArticleConditionForm(
    { articleId },
    { enabled: !!workflow?.conditionForms?.find(({ type }) => type === 'initial_condition') }
  );
  const { data: qualityControlFormData } = useArticleConditionForm(
    {
      articleId,
      type: 'quality_control',
    },
    {
      enabled:
        !!workflow?.conditionForms?.find(({ type }) => type === 'quality_control') &&
        !!article?.steps?.find(
          ({ step, status }) =>
            step === 'quality-control' && ['done', 'in-progress', 'cancelled'].includes(status)
        ),
    }
  );
  const productAttributesByArticle = useProductAttributesByArticle(request);

  // Clear router blocker state after coming from requests/new
  useResetSimpleBlocker();

  if (isRequestLoading) {
    return (
      <PageLayoutCenter>
        <Loader style={{ height: '40px', width: '40px' }} />
        <Paragraph>
          <Trans id="_general.loading">Loading...</Trans>
        </Paragraph>
      </PageLayoutCenter>
    );
  }

  if (!request || !article) {
    return (
      <PageLayoutCenter>
        <Paragraph>
          <Trans id="article.unknown">Unknown article</Trans>
        </Paragraph>
      </PageLayoutCenter>
    );
  }

  return (
    <RequestContextProvider
      request={request}
      workflow={workflow}
      initialConditionFormsData={[
        {
          articleId: article.id,
          formData: conditionFormData,
        },
      ]}
      qualityControlFormsData={[{ articleId: article.id, formData: qualityControlFormData }]}
      productAttributesData={productAttributesByArticle}
    >
      <ArticleContextProvider article={article}>
        <ArticleContent request={request} article={article} />
        <ClientCommunicationDrawer />
      </ArticleContextProvider>
    </RequestContextProvider>
  );
};

const ArticleContent = ({
  request,
  article,
}: {
  request: RequestWithRelations;
  article: ArticleWithRelations;
}) => {
  const navigate = useNavigate();
  const { isMobile } = useViewPort();
  const { view, state } = useArticleContext();
  const { currentSession } = useCurrentSession();
  const [commentsRef, scrollCommentsIntoView] = useScrollIntoView<HTMLDivElement>();
  const [containerRef, scrollContainerIntoView] = useScrollIntoView<HTMLDivElement>();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const articleName = useArticleName({ article });

  // Display the request reference in the browser tab title
  useEffect(() => {
    if (!request) {
      return;
    }

    const requestDetails = request.client?.name ?? request.store?.name;
    const requestReference = requestDetails
      ? `${request.reference} - ${articleName}`
      : request.reference;

    document.querySelector('meta[name="subtitle"]')?.setAttribute('content', requestReference);
    refreshBrowserTabTitle();

    return () => {
      document.querySelector('meta[name="subtitle"]')?.setAttribute('content', '');
      refreshBrowserTabTitle();
    };
  }, [request, articleName]);

  const isOrganizationUser = getOrganizations(currentSession)?.some(
    (organization) => organization.id === article?.organizationId
  );

  return (
    <PageLayout className={block()}>
      <PageLayoutHeader>
        <ArticleHeader
          request={request}
          article={article}
          onCommentButtonPress={scrollCommentsIntoView}
        />
      </PageLayoutHeader>

      <PageLayoutContent>
        <PageLayoutLeftPart>
          <PageLayoutLeftPartContent>
            {isMobile && (
              <PageLayoutTopPart>
                <CustomsAlertBar />
                <ArticlePriceAndEstimatedDueDate />
                <Button
                  style={{ flex: 1 }}
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    setIsSidePanelOpen(true);
                  }}
                >
                  <IconFile />
                  <Trans id="article.information.drawer.open-button.title">
                    View item information
                  </Trans>
                </Button>
                <ArticleInfoDrawer isOpen={isSidePanelOpen} onOpenChange={setIsSidePanelOpen} />
              </PageLayoutTopPart>
            )}

            {!isMobile && (
              <>
                <CustomsAlertBar />
                <RequestBreadcrumb
                  request={request}
                  article={article}
                  showItemSheetPreview={view.itemSheet.preview.enabled}
                  onCommentButtonPress={scrollCommentsIntoView}
                />
              </>
            )}

            <Stack className={element('main')} ref={containerRef}>
              <Stack gap="1rem">
                <ArticleStepper
                  size={isMobile ? 'small' : 'large'}
                  showStepCount={isMobile}
                  article={article}
                  mode="step"
                  labelVariant={undefined}
                />
                <ErrorBoundary>
                  <ArticleTaskOrStepMessage article={article} request={request} />
                </ErrorBoundary>
              </Stack>

              <Stack gap="2rem">
                {article.taskType === 'analyze_article' && (
                  <RequalificationComment article={article} />
                )}

                <WarrantyForm />

                <AuthenticityForm />

                <InitialConditionForm />

                <Defects />

                <Services />

                <QualityControlForm />

                {isOrganizationUser &&
                  getRequestorType(request) !== 'store' &&
                  request.organization.config.clientCommunication?.serviceChoiceComment
                    ?.enabled && (
                    <FoldableSection
                      label={
                        <Title as="h2">
                          <Trans id="article.client-communication">
                            Communication to the client
                          </Trans>
                        </Title>
                      }
                      key={`${article?.step?.step}-${article?.taskType}`}
                      startClosed={article.step?.step !== 'service-choice'}
                    >
                      <Box className="bg-neutral-0">
                        <ClientComment
                          request={request}
                          article={article}
                          disabled={article.taskType !== 'choose_article_service'}
                          key={`client-comment ${article.clientComment ?? ''}`}
                        />
                      </Box>
                    </FoldableSection>
                  )}

                <Stack ref={commentsRef}>
                  <CommentsSection articleId={article.id} requestId={request.id} />
                </Stack>

                <Stack ref={null}>
                  <FoldableSection
                    label={
                      <Title as="h2">
                        <Trans id="article.activity.title">Activity</Trans>
                      </Title>
                    }
                  >
                    <ErrorBoundary>
                      <Box
                        className={isMobile ? undefined : 'bg-neutral-0'}
                        padding="0"
                        removeStyleOnMobile
                      >
                        <ArticleActivities articleId={article.id} />
                      </Box>
                    </ErrorBoundary>
                  </FoldableSection>
                </Stack>
              </Stack>
            </Stack>
          </PageLayoutLeftPartContent>

          <ArticleActions
            article={article}
            request={request}
            onActionDone={(options?: { navigateToRequests?: boolean }) => {
              state.errors.check(null);

              if (options?.navigateToRequests) {
                navigate('/requests');
              } else if (request.articles.filter((article) => !article.archivedAt).length > 1) {
                navigate(`/requests/${request.id}`);
              } else {
                scrollContainerIntoView();
              }
            }}
          />
        </PageLayoutLeftPart>

        {!isMobile && (
          <PageLayoutRightPart>
            <ArticleInfoSidePanel />
          </PageLayoutRightPart>
        )}
      </PageLayoutContent>
    </PageLayout>
  );
};

const Defects = () => {
  const { article, view, errors } = useArticleContext();

  if (!view.defects.shown) {
    return null;
  }

  return (
    <FoldableSection
      label={
        <Title as="h2">
          <Trans id="article.defects">Defects</Trans>
        </Title>
      }
      isInvalid={errors.defects?.hasError}
      startClosed={
        article.step?.step === 'repair' ||
        (article.step?.step === 'quality-control' && article.taskType === 'control_quality') ||
        article.step?.step === 'archival'
      }
      key={`${article?.step?.step}-${article?.taskType}`}
    >
      <Box className="bg-neutral-0">
        <DefectsList />
      </Box>
    </FoldableSection>
  );
};

const Services = () => {
  const { view, errors, article } = useArticleContext();

  if (!view.services.shown) {
    return null;
  }

  return (
    <FoldableSection
      label={
        <Title as="h2">
          <Trans id="article.services">Services</Trans>
        </Title>
      }
      startClosed={
        article.step?.step === 'quality-control' && article.taskType === 'control_quality'
      }
      isInvalid={errors.services?.hasError}
      key={`${article?.step?.step}-${article?.taskType}`}
    >
      <Box className="bg-neutral-0">
        <ArticleServices />
      </Box>
    </FoldableSection>
  );
};

const CustomsAlertBar = () => {
  const { currentSession } = useCurrentSession();
  const { view: requestView } = useRequestContext();
  const { article, state } = useArticleContext();

  if (article.taskType === 'add_customs_informations') {
    return null;
  }

  if (
    article.areCustomsExpected &&
    isMissingCustomsData(article, requestView.customs.type) &&
    hasPermission(currentSession, 'edit_customs_data', [
      {
        organizationId: article.organizationId,
      },
      article.workshopId
        ? {
            entityId: article.workshopId,
          }
        : null,
    ])
  ) {
    const isError = article.step?.step === 'service-choice';

    return (
      <AlertBar type={isError ? 'error' : 'warning'} size="large" icon={<IconPlace warning />}>
        <Stack gap="0.5rem">
          <p>
            <Trans id="article.customs.missing-info">
              The item must go through customs and some required information is missing. Please
              provide the required information.
            </Trans>
          </p>
          <div>
            <Button variant="secondary" onPress={() => state.customsPanel.setIsOpen(true)}>
              <IconEdit />
              <Trans id="article.customs.missing-info.action">Edit customs information</Trans>
            </Button>
          </div>
        </Stack>
      </AlertBar>
    );
  }

  return null;
};
